import { useMemo, useState, useEffect } from "react";
import { useAuth } from "@via/hooks/useAuth.tsx";
import { GetActiveBannerResponse } from "common";
import {
  getDismissedBanners,
  isBannerDismissed,
  dismissBanner as dismissBannerLocalStorage,
} from "@via/utils/banner-dismissal.ts";

interface DismissedBanner {
  id: number;
  updatedAt: string;
}

export const useGetActiveBanner = (): {
  data: GetActiveBannerResponse | undefined | null;
  isLoading: boolean;
} => {
  const { user } = useAuth();
  const [dismissedBanners, setDismissedBanners] = useState<DismissedBanner[]>(
    () => getDismissedBanners()
  );

  // Listen for storage events to sync across tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "viafoundry.dismissedBanners") {
        setDismissedBanners(getDismissedBanners());
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const data = useMemo(() => {
    const banner =
      typeof user === "object" && user && "systemBanner" in user
        ? user.systemBanner
        : null;

    if (!banner) {
      return null;
    }

    // Filter out dismissed banners (check both id and updatedAt)
    if (isBannerDismissed(banner.id, banner.updatedAt)) {
      return null;
    }

    return banner;
  }, [user, dismissedBanners]);

  return {
    data,
    isLoading: user === null,
  };
};

// Dismiss banner - now client-side only using localStorage
export const useDismissBanner = () => {
  const [, setDismissedBanners] = useState<DismissedBanner[]>(() =>
    getDismissedBanners()
  );

  return {
    dismissBanner: (bannerId: number, updatedAt: string) => {
      dismissBannerLocalStorage(bannerId, updatedAt);
      setDismissedBanners(getDismissedBanners());
    },
  };
};
